import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ArrowLeft, Pencil, Info, Loader2 } from 'lucide-react';
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Textarea } from "../components/ui/TextArea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { TagManagement, TagManagementRef } from "../components/TagManagement";
import { FormField } from '../components/FormField';
import { Agent } from '../types';
import ToolsSelect from './ToolsSelect';
import { agentFormHelpText, formatHelpText } from '../config/helptext-config';
import { AccessControlSection } from '../components/AccessControlSection';
import { useAuth } from '../contexts/AuthContext';
import { useUsersApi } from '../apis/users-apis';
import AccessDisplay from '../components/AccessDisplay';
import OwnerSelection from '../components/OwnerSelection';
import { Checkbox } from "../components/ui/Checkbox";
import { ADMIN_SERVICE_USER } from '../config/api-config';

interface AccessControl {
  editAccessType: string;
  editAllowedUsers: string[];
  viewAccessType: string;
  viewAllowedUsers: string[];
}

// View Mode Field Component
const ViewModeField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="mb-6">
    <h3 className="text-sm font-medium text-gray-500 mb-2">{label}</h3>
    {typeof value === 'string' ? (
      <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm whitespace-pre-wrap border border-gray-200">
        {value || 'No content provided'}
      </div>
    ) : (
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        {value}
      </div>
    )}
  </div>
);

interface AgentFormProps {
  agent: Agent;
  originalAgent?: Agent | null;
  onAgentChange: (agent: Agent) => void;
  onSubmit: () => Promise<void>;
  onCancel: () => void;
  isEditing?: boolean;
  readOnly?: boolean;
  onEditClick?: () => void;
  onDiscardChanges?: () => void;
  hasUnsavedChanges?: boolean;
  setIsReadOnly: (value: boolean) => void;
  tagSuggestions?: string[];
  existingAgents: Agent[];
  isSaving?: boolean;
}

export interface AgentFormRef {
  validateForm: () => Promise<boolean>;
  getErrors: () => Record<string, string>;
}

interface UserAPIResponse {
  users: {
    userId: string;
    email: string;
    isAdmin: boolean;
    isActive: boolean;
  }[];
}

const SaveOverlay = () => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6 flex items-center space-x-4">
      <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
      <span className="text-lg font-medium">Saving Agent... Please wait</span>
    </div>
  </div>
);

const AgentForm = forwardRef<AgentFormRef, AgentFormProps>((props, ref) => {
  const {
    agent,
    onAgentChange,
    onSubmit,
    onCancel,
    isEditing = false,
    readOnly = false,
    onEditClick,
    onDiscardChanges,
    hasUnsavedChanges = false,
    tagSuggestions = [],
    existingAgents
  } = props;

  const [errors, setErrors] = useState<Record<string, string>>({});
  const { user, isAdmin } = useAuth();
  const [isCurrentUserAdmin, setIsCurrentUserAdmin] = useState(false);
  const { getUsers } = useUsersApi();
  const tagManagementRef = useRef<TagManagementRef>(null);
  const [isCoreAgent, setIsCoreAgent] = useState(agent.isCore || false);
  const [adminServiceUserId, setAdminServiceUserId] = useState<string>('');

  useEffect(() => {
    const fetchAdminStatus = async () => {
      if (user?.sub) {
        try {
          const usersData = await getUsers();
          const userData = usersData.find(u => u.userId === user.sub);
          setIsCurrentUserAdmin(userData?.isAdmin || false);
          
          // Find the admin service account ID
          const adminServiceAccount = usersData.find(u => 
            u.email === ADMIN_SERVICE_USER
          );
          
          if (adminServiceAccount) {
            setAdminServiceUserId(adminServiceAccount.userId);
          } else {
            console.warn(`Admin service account ${ADMIN_SERVICE_USER} not found in users list`);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setIsCurrentUserAdmin(false);
        }
      }
    };

    fetchAdminStatus();
  }, [user?.sub]);

  useEffect(() => {
    // When isCoreAgent is enabled, set default access control to "Owner Only"
    // and set owner to admin service account
    if (isCoreAgent) {
      onAgentChange({
        ...agent,
        isCore: true,
        ownerId: adminServiceUserId || agent.ownerId,
        accessControl: {
          viewAccessType: 'Owner Only',
          viewAllowedUsers: [],
          editAccessType: 'Owner Only',
          editAllowedUsers: []
        }
      });
    } else {
      onAgentChange({
        ...agent,
        isCore: false
      });
    }
  }, [isCoreAgent, adminServiceUserId]);

  const hasEditAccess = (accessControl: AccessControl) => {
    const currentUserId = user?.sub;
    const isOwner = agent.ownerId === currentUserId;
    
    if (!currentUserId) return false;
    
    // Admins always have edit access
    if (isCurrentUserAdmin) return true;
    
    // Owners always have edit access to their own agents
    if (isOwner) return true;
    
    // For non-admin, non-owner users, check access control settings
    const { editAccessType, editAllowedUsers } = accessControl;
    
    switch (editAccessType) {
      case 'All Users':
        return true;
      case 'Allowed Users':
        return editAllowedUsers.includes(currentUserId);
      case 'Owner Only':
        return false;
      default:
        return false;
    }
  };

  // Add initial access control if not present
  if (!agent.accessControl) {
    agent.accessControl = {
      viewAccessType: 'Owner Only',
      viewAllowedUsers: [],
      editAccessType: 'Owner Only',
      editAllowedUsers: []
    };
  }

  const checkNameExists = (name: string): boolean => {
    return existingAgents.some(existingAgent => 
      existingAgent.agentName.toLowerCase() === name.toLowerCase() && 
      existingAgent.agentId !== agent.agentId
    );
  };

  useImperativeHandle(ref, () => ({
    validateForm: async () => {
      const newErrors: Record<string, string> = {};
  
      const nameValidation = await validateAgentName(props.agent.agentName);
      if (!nameValidation.isValid) {
        newErrors.agentName = nameValidation.error;
      }
  
      if (!props.agent.agentDescription.trim()) {
        newErrors.agentDescription = 'Agent Description is required';
      }
      
      if (!props.agent.agentPrompt.trim()) {
        newErrors.agentPrompt = 'Agent Prompt is required';
      }

      // Add access control validation only if not a core agent
      if (!isCoreAgent) {
        if (!props.agent.accessControl.viewAccessType) {
          newErrors.viewAccessType = 'View Access Type is required';
        }

        if (!props.agent.accessControl.editAccessType) {
          newErrors.editAccessType = 'Edit Access Type is required';
        }
      }
  
      setErrors(newErrors);

      // If validation passed, process any pending tags
      if (Object.keys(newErrors).length === 0) {
        // Process pending tags if any
        if (tagManagementRef.current) {
          await tagManagementRef.current.processPendingTags();
        }
      }

      return Object.keys(newErrors).length === 0;
    },
    getErrors: () => errors
  }), [props.agent, errors, isCoreAgent]);

  const validateAgentName = async (name: string): Promise<{ isValid: boolean; error: string }> => {
    if (!name.trim()) {
      return { isValid: false, error: 'Agent Name is required' };
    }

    if (name.length > 63) {
      return { isValid: false, error: 'Agent Name must be less than 64 characters' };
    }
    
    // Check for disallowed characters (hyphens and spaces)
    if (name.includes('-') || name.includes(' ')) {
      return { 
        isValid: false, 
        error: 'Agent Name can only contain letters, numbers, and underscores. Hyphens and spaces are not allowed'
      };
    }

    // Check for uppercase letters - for CamelCase validation
    if (!/^[A-Z][a-zA-Z]*$/.test(name)) {
      return {
        isValid: false,
        error: 'Agent Name must be in CamelCase format starting with an uppercase letter'
      };
    }

    // Check for uniqueness
    if (checkNameExists(name)) {
      return {
        isValid: false,
        error: 'An Agent with this name already exists. Please choose a different name.'
      };
    }
  
    return { isValid: true, error: '' };
  };

  const handleInputChange = async (field: keyof Agent, value: any) => {
    if (readOnly) return;
    
    onAgentChange({
      ...agent,
      [field]: value
    });
  
    // Immediate validation for agentName field
    if (field === 'agentName') {
      const validation = await validateAgentName(value);
      if (!validation.isValid) {
        setErrors(prev => ({
          ...prev,
          agentName: validation.error
        }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.agentName;
          return newErrors;
        });
      }
    }
  };

  const handleCoreAgentChange = (checked: boolean) => {
    if (readOnly) return;
    setIsCoreAgent(checked);
  };

  const AccessControlViewSection = () => {
    const [ownerDetails, setOwnerDetails] = useState({ email: '', name: '' });
    const { getUsers } = useUsersApi();
    
    useEffect(() => {
      const fetchOwnerDetails = async () => {
        try {
          const users = await getUsers();
          const owner = users.find((user) => user.userId === agent.ownerId);
          if (owner) {
            setOwnerDetails({
              email: owner.email,
              name: owner.email //TODO: Add name to user table
            });
          }
        } catch (error) {
          console.error('Error fetching owner details:', error);
        }
      };
      
      fetchOwnerDetails();
    }, [agent.ownerId]);

    // For Core Agents, show a special message
  if (agent.isCore) {
    return (
      <ViewModeField
        label="Access Control"
        value={
          <div className="flex items-center space-x-2 text-gray-700">
            <Info className="h-5 w-5 text-blue-500" />
            <div>
              <p className="font-medium">Administrator Only</p>
              <p className="text-sm text-gray-600">
                This Core Agent is managed by administrators only.
              </p>
            </div>
          </div>
        }
      />
    );
  }
  
    return (
      <ViewModeField
        label="Access Control"
        value={
          <AccessDisplay
            viewAccessType={agent.accessControl.viewAccessType}
            editAccessType={agent.accessControl.editAccessType}
            viewAllowedUsers={agent.accessControl.viewAccessType === 'Allowed Users' 
              ? agent.accessControl.viewAllowedUsers 
              : []}
            editAllowedUsers={agent.accessControl.editAccessType === 'Allowed Users' 
              ? agent.accessControl.editAllowedUsers 
              : []}
            ownerEmail={ownerDetails.email}
            ownerName={ownerDetails.name}
          />
        }
      />
    );
  };

  const ListDisplay: React.FC<{ 
    items: any[]; 
    type: 'tool' | 'agent';
  }> = ({ items, type }) => {
    if (items.length === 0) {
      return <div className="text-gray-500 italic">No {type}s configured</div>;
    }
  
    return (
      <div className="space-y-4">
        {items.map((item) => (
          <div key={type === 'tool' ? item.toolId : item.agentId} 
               className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="font-medium text-gray-900">
              {type === 'tool' ? item.toolName : item.agentName}
            </div>
            <p className="text-gray-600 mt-1">
              {type === 'tool' ? item.toolDescription : item.agentDescription}
            </p>
          </div>
        ))}
      </div>
    );
  };

  const renderEditMode = () => (
    <Card className="max-w-3xl mx-auto">
      <CardHeader className="flex flex-col gap-4">
  <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full">
    <CardTitle className="text-2xl">
      {isEditing ? 'Edit Agent' : 'Add New Agent'}
    </CardTitle>
    <Button
      variant="outline"
      onClick={onCancel}
      className="border-blue-500 text-blue-500 hover:bg-blue-50 hover:text-blue-600 mt-2 sm:mt-0"
    >
      <ArrowLeft className="mr-2 h-4 w-4" />
      Back to Agents
    </Button>
  </div>
</CardHeader>

      <CardContent className="space-y-8">
        <FormField
          label="Agent Name"
          helpText={formatHelpText(agentFormHelpText.agentName)}
          required
          error={errors.agentName}
        >
          <Input
            value={agent.agentName}
            onChange={(e) => {
              const newValue = e.target.value;
              handleInputChange('agentName', newValue);
            }}
            placeholder="Enter agent name"
            className={errors.agentName ? 'border-red-500' : ''}
          />
        </FormField>

        <FormField
          label="Agent Description"
          helpText={formatHelpText(agentFormHelpText.agentDescription)}
          required
          error={errors.agentDescription}
        >
          <Textarea
            value={agent.agentDescription}
            onChange={(e) => handleInputChange('agentDescription', e.target.value)}
            placeholder="Enter agent description"
            rows={3}
            className={errors.agentDescription ? 'border-red-500' : ''}
          />
        </FormField>

        <FormField
          label="Agent Tags"
          helpText={formatHelpText(agentFormHelpText.agentTags)}
        >
          <TagManagement
            ref={tagManagementRef}
            tags={agent.agentTags}
            onChange={(newTags) => handleInputChange('agentTags', newTags)}
            readOnly={false}
            suggestions={tagSuggestions}
            className="w-full"
          />
        </FormField>

        <FormField
          label="Agent Prompt"
          helpText={formatHelpText(agentFormHelpText.agentPrompt)}
          required
          error={errors.agentPrompt}
        >
          <Textarea
            value={agent.agentPrompt}
            onChange={(e) => handleInputChange('agentPrompt', e.target.value)}
            placeholder="Enter agent prompt"
            rows={5}
            className={errors.agentPrompt ? 'border-red-500' : ''}
          />
        </FormField>

        <FormField
          label="Tools"
          helpText={formatHelpText(agentFormHelpText.agentSelectedTools)}
        >
          <ToolsSelect
            selectedTools={agent.agentSelectedTools}
            onChange={(tools) => handleInputChange('agentSelectedTools', tools)}
            readOnly={readOnly}
          />
        </FormField>

        {isCurrentUserAdmin && (
          <div className="flex items-start space-x-2 border p-4 rounded-md bg-gray-50">
            <Checkbox 
              id="core-agent" 
              checked={isCoreAgent}
              onCheckedChange={handleCoreAgentChange}
            />
            <div className="flex flex-col">
              <label 
                htmlFor="core-agent" 
                className="text-sm font-medium cursor-pointer"
              >
                Core Agent (Admin Only)
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Core agents have fixed access control set to "Owner Only", will be owned by the admin service account, and cannot be modified by regular users.
              </p>
            </div>
          </div>
        )}

        {!isCoreAgent && (
          <AccessControlSection<Agent>
            entity={agent}
            entityType="Agent"
            handleInputChange={handleInputChange}
            errors={errors}
          />
        )}

        {isEditing && isCurrentUserAdmin && !isCoreAgent && (
          <OwnerSelection
            currentOwnerId={agent.ownerId}
            onOwnerChange={(newOwnerId) => handleInputChange('ownerId', newOwnerId)}
            isReadOnly={readOnly}
          />
        )}
        
        {isEditing && isCurrentUserAdmin && isCoreAgent && adminServiceUserId && (
          <div className="border p-4 rounded-md bg-blue-50">
            <h3 className="text-sm font-medium text-blue-800 mb-2">Owner</h3>
            <p className="text-sm text-blue-700">
              This Core Agent will be owned by the admin service account.
            </p>
          </div>
        )}

<div className="flex justify-end gap-2 pt-4">
  {isEditing && onDiscardChanges && (
    <Button
      variant="outline"
      onClick={onDiscardChanges}
      className="border-gray-300 text-gray-700 hover:bg-gray-50"
      disabled={props.isSaving}
    >
      Cancel Edit
    </Button>
  )}
  <Button
    variant="default"
    onClick={onSubmit}
    className="bg-blue-500 hover:bg-blue-600 text-white"
    disabled={props.isSaving}
  >
    {props.isSaving ? (
      <div className="flex items-center space-x-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Saving...</span>
      </div>
    ) : (
      'Save'
    )}
  </Button>
</div>
      </CardContent>
    </Card>
  );

  const renderViewMode = () => (
    <Card className="max-w-3xl mx-auto">
      <CardHeader className="flex flex-col gap-4 pb-2">
  <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full">
    <CardTitle className="text-2xl break-words max-w-full overflow-hidden text-ellipsis">
      {agent.agentName}
    </CardTitle>
    <div className="flex gap-2 mt-2 sm:mt-0 flex-shrink-0">
      {onEditClick && hasEditAccess(agent.accessControl) && (
        <Button
          variant="outline"
          onClick={onEditClick}
          className="border-blue-500 text-blue-500 hover:bg-blue-50 hover:text-blue-600"
        >
          <Pencil className="mr-2 h-4 w-4" />
          Edit
        </Button>
      )}
      <Button
        variant="outline"
        onClick={onCancel}
        className="border-blue-500 text-blue-500 hover:bg-blue-50 hover:text-blue-600"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Agents
      </Button>
    </div>
  </div>
</CardHeader>

      <CardContent className="space-y-8">
        <ViewModeField 
          label="Description" 
          value={agent.agentDescription} 
        />
        
        <ViewModeField 
          label="Prompt" 
          value={agent.agentPrompt} 
        />
        
        <ViewModeField 
          label="Tools" 
          value={
            <ListDisplay 
              items={agent.agentSelectedTools || []} 
              type="tool" 
            />
          }
        />
        
        <ViewModeField 
          label="Tags" 
          value={
            <TagManagement
              tags={agent.agentTags}
              onChange={() => {}}
              readOnly={true}
              suggestions={tagSuggestions}
            />
          } 
        />

        {/* Show Access Control section for all agents, with special handling for Core Agents inside the component */}
        <AccessControlViewSection />



      </CardContent>
    </Card>
  );

  return (
    <div className="container mx-auto p-4" data-has-unsaved-changes={hasUnsavedChanges}>
      {props.isSaving && <SaveOverlay />}
      {readOnly ? renderViewMode() : renderEditMode()}
    </div>
  );
});

export default AgentForm;